import { Link } from 'react-router-dom';
import { Triangle as TriangleIcon, AlertTriangle } from 'lucide-react';

/**
 * TriangleScoreChip
 * -----------------
 * Compact chip showing a stock's Dividend Triangle Score (0-100). Rendered
 * inline next to other stock badges on Watchlist / Screener rows.
 *
 * Only renders when the stock row already has `triangle_score` set —
 * which happens lazily after any user views the Triangle tab for that
 * ticker (we mirror the score onto `stock_cache` from
 * `_compute_triangle_payload`). New rows show nothing until first view.
 *
 * VARIANTS
 *   • Full triangle (all 3 legs scored) — solid-bordered pill with the
 *     ▲ icon and the score. This is the "real" Triangle Score.
 *   • Partial score (only 1-2 legs available, the rest were null in
 *     yfinance) — dashed-bordered pill with NO triangle icon and a
 *     leading `~` to signal "approximate / not the full radar". The
 *     visual ranks alongside the Quality Score letter pill rather than
 *     pretending to be a complete Triangle.
 * The chip also shows a small AlertTriangle when `triangle_trap_flag`
 * is true, regardless of variant.
 *
 * Color follows the same band as the scorecard hero:
 *   ≥70 emerald · ≥50 sky · ≥35 amber · <35 rose
 * Trap-flagged rows render in rose regardless of score.
 *
 * Click navigates to `/triangle/:ticker` (the standalone shareable view).
 * stopPropagation prevents the row click from also opening the stock modal.
 */
export default function TriangleScoreChip({ stock, onTriangleNav }) {
  const score = stock?.triangle_score;
  const trap = !!stock?.triangle_trap_flag;
  if (score === null || score === undefined) return null;

  // `triangle_legs` is the count of legs (0-3) that had real CAGR data
  // when the score was computed. Older cache entries don't carry this
  // field; assume "full" (3) for backwards compatibility so existing
  // chips don't suddenly switch to the partial style.
  const legs = stock?.triangle_legs == null ? 3 : Number(stock.triangle_legs);
  const isPartial = legs > 0 && legs < 3;

  let tone;
  if (trap) tone = 'rose';
  else if (score >= 70) tone = 'emerald';
  else if (score >= 50) tone = 'sky';
  else if (score >= 35) tone = 'amber';
  else tone = 'rose';

  const TONE = {
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60',
    sky: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60',
    rose: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60',
  }[tone];

  const titleBase = trap
    ? `Dividend Triangle Score ${score}/100 · ⚠ potential dividend trap.`
    : `Dividend Triangle Score ${score}/100.`;
  const title = isPartial
    ? `${titleBase} Only ${legs} of 3 legs available — partial data, treat as approximate. Click for details.`
    : `${titleBase} Click for the full Revenue / EPS / Dividend radar.`;

  return (
    <Link
      to={`/triangle/${stock.ticker}`}
      onClick={(e) => {
        e.stopPropagation();
        if (onTriangleNav) onTriangleNav(stock.ticker);
      }}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border hover:brightness-110 transition ${TONE} ${isPartial ? 'border-dashed' : ''}`}
      title={title}
      data-testid={`triangle-chip-${stock.ticker}`}
      aria-label={title}
    >
      <TriangleIcon className="w-2.5 h-2.5" />
      {score}
      {trap && <AlertTriangle className="w-2.5 h-2.5 ml-0.5" />}
    </Link>
  );
}
